/**
 * SalesLotPieChart — donut du CA lignes de la période par lot d'origine (SKU LT<n>).
 *
 * Top N lots par CA, le reste replié en « Autres », les SKU sans lot en « Hors lot ».
 * CA ligne = prix × quantité − remise, hors frais de port : le total ne coïncide
 * donc pas exactement avec le CA TTC des commandes.
 */
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Package } from 'lucide-react';
import { formatCurrency, formatCurrencyShort } from '../lib/format.js';

// Palette catégorielle (ordre fixe, validée CVD) ; les neutres ne portent pas d'identité.
export const LOT_SERIES_COLORS = [
  '#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948',
];
const OTHERS_COLOR = '#94a3b8';
const NO_LOT_COLOR = '#cbd5e1';

const sum = (rows, key) => rows.reduce((acc, r) => acc + Number(r[key] ?? 0), 0);

export function buildLotSlices(rows, { topN = LOT_SERIES_COLORS.length } = {}) {
  const list = Array.isArray(rows) ? rows.filter((r) => Number(r.ca_cents) > 0) : [];
  const totalCa    = sum(list, 'ca_cents');
  const totalItems = sum(list, 'items_count');

  const noLot = list.find((r) => r.key === 'other');
  const lots  = list
    .filter((r) => r.key !== 'other')
    .sort((a, b) => Number(b.ca_cents) - Number(a.ca_cents));

  const keep = Math.min(topN, LOT_SERIES_COLORS.length);
  const slices = lots.slice(0, keep).map((r, i) => ({ ...r, color: LOT_SERIES_COLORS[i] }));

  const rest = lots.slice(keep);
  if (rest.length > 0) {
    slices.push({
      key:         'others',
      lot_number:  null,
      label:       `Autres (${rest.length} lot${rest.length > 1 ? 's' : ''})`,
      ca_cents:    sum(rest, 'ca_cents'),
      items_count: sum(rest, 'items_count'),
      color:       OTHERS_COLOR,
    });
  }
  if (noLot) slices.push({ ...noLot, color: NO_LOT_COLOR });

  return {
    slices: slices.map((s) => ({
      ...s,
      ca_cents:    Number(s.ca_cents),
      items_count: Number(s.items_count),
      pct:         totalCa > 0 ? (Number(s.ca_cents) / totalCa) * 100 : 0,
    })),
    total_ca_cents: totalCa,
    total_items:    totalItems,
  };
}

const pluralPieces = (n) => `${n.toLocaleString('fr-FR')} pièce${n > 1 ? 's' : ''}`;
const formatPct    = (pct) => `${pct.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;

function LotTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const s = payload[0].payload;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-slate-200 px-3 py-2.5 text-xs min-w-[170px]">
      <p className="font-semibold text-slate-700 mb-1.5 pb-1.5 border-b border-slate-100 flex items-center gap-1.5">
        <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: s.color }} />
        {s.label}
      </p>
      <div className="flex items-center justify-between gap-3">
        <span className="text-slate-500">CA</span>
        <span className="tabular-nums font-semibold text-slate-900">{formatCurrency(s.ca_cents)}</span>
      </div>
      <div className="flex items-center justify-between gap-3 mt-0.5">
        <span className="text-slate-500">Pièces</span>
        <span className="tabular-nums text-slate-700">{pluralPieces(s.items_count)}</span>
      </div>
      <p className="text-slate-400 mt-1">{formatPct(s.pct)} du CA lignes</p>
    </div>
  );
}

export default function SalesLotPieChart({ data, loading, error }) {
  if (loading) {
    return <div className="h-[280px] bg-slate-100 animate-pulse rounded-lg" />;
  }

  const { slices, total_ca_cents, total_items } = buildLotSlices(data);

  if (slices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 h-[200px]">
        <Package size={28} className="text-slate-300" />
        <p className="text-sm text-slate-400">Aucune pièce vendue sur cette période.</p>
        {error && <p className="text-xs text-red-600">{error.message}</p>}
      </div>
    );
  }

  return (
    <div>
      <div className="relative h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="ca_cents"
              nameKey="label"
              innerRadius="62%"
              outerRadius="90%"
              paddingAngle={1}
              stroke="#ffffff"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {slices.map((s) => <Cell key={s.key} fill={s.color} />)}
            </Pie>
            <Tooltip content={<LotTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <p className="text-lg font-bold tabular-nums text-slate-900 leading-tight">
            {formatCurrencyShort(total_ca_cents)}
          </p>
          <p className="text-[11px] text-slate-400">{pluralPieces(total_items)}</p>
        </div>
      </div>

      <ul className="mt-3 space-y-1">
        {slices.map((s) => (
          <li key={s.key} className="flex items-center gap-2 text-xs">
            <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: s.color }} />
            <span className="text-slate-700 truncate flex-1 min-w-0">{s.label}</span>
            <span className="tabular-nums text-slate-500 shrink-0">{formatCurrencyShort(s.ca_cents)}</span>
            <span className="tabular-nums text-slate-400 w-14 text-right shrink-0">{formatPct(s.pct)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
